import React, { useState, useEffect, useRef, useContext } from "react";
import { MessageSquare, MessageCircle, Phone, Video, Disc, Search, Loader2 } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import RecordingsPanel from "./RecordingsPanel";
import CallHistoryPanel from "./CallHistoryPanel";
import NewChatModal from "./NewChatModal";
import { OPEN_RECORDINGS_EVENT } from "./recordingLink";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const LeftChatList = ({ onSelectDM, onStartCall }) => {
  const { user } = useContext(AuthContext);

  const [search,       setSearch]       = useState("");
  const [activeChat,   setActiveChat]   = useState(null);
  const [users,        setUsers]        = useState([]);   // recent conversations
  const [directory,    setDirectory]    = useState([]);   // full org roster
  const [dirError,     setDirError]     = useState(false); // directory fetch failed
  const [showNewChat,  setShowNewChat]  = useState(false); // "+" new-chat modal
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [callingId,    setCallingId]    = useState(null); // user id currently being called
  const callingRef = useRef(false);
  const [unreadIds,    setUnreadIds]    = useState(new Set()); // user ids with unread messages
  const [showRecordings,   setShowRecordings]   = useState(false);

  const [showCallHistory,  setShowCallHistory]  = useState(null);

  // A recording link in a DM opens this panel rather than navigating to
  // the backend URL — see RenderText in DirectMessageThread.jsx for why.
  // A window event rather than a prop: the two components are siblings
  // under ChatScreen and neither owns the other, and this keeps the
  // change to the recording link itself.
  useEffect(() => {
    const open = () => { setShowRecordings(true); setShowCallHistory(null); };
    window.addEventListener(OPEN_RECORDINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_RECORDINGS_EVENT, open);
  }, []);

  // ── Load recent conversations + full org directory ─────────
  // Recent conversations (with last message / unread) drive the default view;
  // the org directory lets search reach ANY colleague, even ones you've never
  // messaged. Both are fetched once on mount — the directory is small.
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");

    const fetchData = async () => {
      setLoadingUsers(true);
      setDirError(false);

      // 1) Recent conversations — existing DM contacts, ordered, with previews.
      try {
        const res = await fetch(`${API_BASE}/api/dm/conversations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setUsers(data?.conversations || []);
        }
      } catch (e) {
        console.warn("Could not load conversations:", e);
      }

      // 2) Full org directory — active users in this org (incl. pending /
      //    not-yet-logged-in colleagues), excludes self. Powers search.
      try {
        const res = await fetch(`${API_BASE}/api/users/directory`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`directory ${res.status}`);
        const d = await res.json();
        // /api/users/directory → { ok, message, data: [...] }; tolerate shapes.
        const list = Array.isArray(d) ? d : (d?.data || d?.users || []);
        setDirectory(list.filter((u) => !u.is_self));
      } catch (e) {
        console.warn("Could not load org directory:", e);
        setDirError(true);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSelectDM = (dm) => {
    setActiveChat(dm.id);
    setUnreadIds(prev => { const n = new Set(prev); n.delete(String(dm.id)); return n; });
    onSelectDM?.(dm);
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d    = new Date(iso);
    const now  = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (diff === 1) return "Yesterday";
    if (diff < 7)  return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString([], { day: "2-digit", month: "short" });
  };

  const roleStr = (role) =>
    typeof role === "object" && role !== null ? (role.value || "") : (role || "");

  const matchesQuery = (u, q) =>
    (u.full_name || u.name || "").toLowerCase().includes(q) ||
    (u.email     || "").toLowerCase().includes(q) ||
    roleStr(u.role).toLowerCase().includes(q);

  const q = search.trim().toLowerCase();

  // Default view (no search): recent conversations only.
  // On search: union of {conversations, directory}, de-duplicated by user id —
  // the conversation record wins so last-message / unread context is preserved.
  let filtered;
  if (!q) {
    filtered = users;
  } else {
    const byId = new Map();
    directory.forEach((u) => byId.set(String(u.id), u));
    users.forEach((c) => byId.set(String(c.id), c)); // conversation overrides directory
    filtered = Array.from(byId.values()).filter((u) => matchesQuery(u, q));
  }

  // ── Presence socket ownership ──
  // The single per-user presence socket lives in CallProvider (via
  // useCallNotifications → /api/calling/presence). LeftChatList previously
  // opened a SECOND socket to /api/dm/presence — a route the backend never
  // exposed, so it only ever logged a failed-connection error and never
  // delivered a message (unreadIds was populated exclusively here, so unread
  // dots never lit up). Removed: no live DM-unread channel exists to consume,
  // and the provider already owns the one real presence connection. If live
  // unread badges are wanted later, the backend must push DM events on the
  // presence channel and CallProvider can surface them via context.

  // ── Start a call — room creation + invite + busy handling now live in the
  //    app-wide CallProvider (provider.startCall via onStartCall). We only own
  //    the per-row "calling…" spinner. ──
  const handleCall = async (targetUser, audioOnly = false) => {
    if (callingRef.current) return;
    callingRef.current = true;
    setCallingId(String(targetUser.id));
    try {
      await onStartCall?.({ targetUser, audioOnly });
    } catch (err) {
      console.error("Failed to start call:", err);
    } finally {
      callingRef.current = false;
      setCallingId(null);
    }
  };

  // ── Quick call from icon row (no specific target) ───────────
  const handleQuickCall = (audioOnly) => {
    onStartCall?.({ targetUser: null, audioOnly });
  };

  return (
    <div className="p-4 flex flex-col h-full" style={{ position:"relative" }}>

      {/* Mode switcher — restyled to messenger_a2 spec: 34px icons, green-glow
          active state, hover tooltip pills. Wired to the SAME panel-toggle
          logic the icons already used (Chats / Audio / Video / Recordings).
          The panels remain the absolute overlays below, so they still start at
          top:64px — the row keeps that 64px height (16 top pad + 34 icon + 14
          margin). Per spec the "+ New" and inert "Group" icons are removed. */}
      <style>{MSGR_MODE_CSS}</style>
      <div
        className="kwm-modes"
        style={{
          position:     "relative",
          zIndex:       102,
          background:   "#0B0B0B",
          marginBottom: 14,
        }}
      >
        <ModeIcon Icon={MessageSquare} label="Chats"
          active={!showCallHistory && !showRecordings}
          onClick={() => { setShowCallHistory(null); setShowRecordings(false); }} />
        <ModeIcon Icon={Phone} label="Audio calls"
          active={showCallHistory === "audio"}
          onClick={() => { setShowCallHistory("audio"); setShowRecordings(false); }} />
        <ModeIcon Icon={Video} label="Video calls"
          active={showCallHistory === "video"}
          onClick={() => { setShowCallHistory("video"); setShowRecordings(false); }} />
        <ModeIcon Icon={Disc} label="Recordings"
          active={showRecordings}
          onClick={() => { setShowRecordings(true); setShowCallHistory(null); }} />
      </div>

      {/* Pane title follows the active mode. Sits below the 64px overlay line,
          so the call/recording panels (which carry their own headers) cover it
          when open — it reads for the default Chats view. */}
      <h2 style={{
        fontSize: 18, fontWeight: 600, letterSpacing: "-0.02em",
        margin: "0 2px 10px", color: "#fff",
      }}>
        {showRecordings ? "Recordings"
          : showCallHistory === "audio" ? "Audio calls"
          : showCallHistory === "video" ? "Video calls"
          : "Messages"}
      </h2>

      {/* Search — rounded pill per spec */}
      <div className="relative mb-3">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color:"rgba(255,255,255,0.35)" }}><Search size={14} strokeWidth={1.9} /></span>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-full pl-9 pr-8 py-2 text-sm outline-none placeholder-white/35 transition"
          style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.10)", color: "#fff" }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(0,255,153,0.30)")}
          onBlur={(e)  => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)")}
          placeholder="Search by name or email…" />
        {search && (
          <button onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 text-xs">
            ✕
          </button>
        )}
      </div>

      {/* DM list — real users */}
      <div className="space-y-2 flex-1 overflow-y-auto pr-1">
        {loadingUsers && <p className="text-[10px] text-white/30 text-center py-4">Loading users…</p>}

        {!loadingUsers && filtered.map((u) => {
          const name     = u.full_name || u.name || u.email || "Unknown";
          const _parts   = name.trim().split(/\s+/).filter(Boolean);
          const inits    = ((_parts[0]?.[0] || "?") + (_parts.length > 1 ? _parts[_parts.length - 1][0] : "")).toUpperCase();
          const subtitle = u.designation || u.title || u.department || (typeof u.role === "object" ? u.role?.value : u.role) || "";
          const isCalling = callingId === String(u.id);
          const lastMsg   = u.last_message || "";
          const isActive  = activeChat === String(u.id);
          const isUnread  = unreadIds.has(String(u.id));
          const rowTime   = formatTime(
            u.last_message_at || u.ts || u.updated_at || u.last_message_time
          );

          return (
            <div key={u.id}
              className="group relative flex items-center gap-2.5 transition"
              style={{
                padding:      "9px 10px",
                borderRadius: 14,
                border:       `1px solid ${isActive ? "rgba(0,255,153,0.30)" : "transparent"}`,
                background:   isActive ? "rgba(0,255,153,0.12)" : "transparent",
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
            >

              {/* Avatar + presence dot (static green — backend exposes no live
                  presence) + green unread dot (boolean signal, no counts) */}
              <div className="relative flex-shrink-0">
                {u.avatar || u.profile_picture ? (
                  <img src={u.avatar || u.profile_picture} alt={name}
                    className="w-9 h-9 rounded-full object-cover"
                    style={{ border: "1.5px solid rgba(0,255,153,0.4)" }} />
                ) : (
                  <div style={{
                    width:          38,
                    height:         38,
                    borderRadius:   "50%",
                    background:     "#131313",
                    border:         "1px solid rgba(255,255,255,0.10)",
                    color:          "#00FF99",
                    fontSize:       12,
                    fontWeight:     600,
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "center",
                    textTransform:  "uppercase",
                    flexShrink:     0,
                  }}>
                    {inits}
                  </div>
                )}
                <span style={{ position:"absolute", bottom:0, right:0, width:9, height:9, borderRadius:99, background:"#00FF99", border:"2px solid #050505" }} />
                {isUnread && (
                  <span style={{ position:"absolute", top:-2, right:-2, width:10, height:10, borderRadius:99, background:"#00FF99", border:"2px solid #050505", boxShadow:"0 0 6px rgba(0,255,153,0.6)" }} />
                )}
              </div>

              {/* Name + time + one-line preview */}
              <div className="flex-1 min-w-0 cursor-pointer"
                onClick={() => handleSelectDM({ id: u.id, name, user: u })}>
                <div style={{ display:"flex", justifyContent:"space-between", gap:8, alignItems:"baseline" }}>
                  <span style={{ fontSize:13, fontWeight:600, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{name}</span>
                  {rowTime && <time style={{ fontSize:10, color:"rgba(255,255,255,0.35)", fontWeight:500, flexShrink:0 }}>{rowTime}</time>}
                </div>
                <p style={{
                  fontSize:11.5, margin:"2px 0 0",
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                  color: isUnread ? "#fff" : "rgba(255,255,255,0.55)",
                  fontWeight: isUnread ? 500 : 400,
                }}>
                  {isCalling ? "📞 Calling…" : lastMsg || subtitle}
                </p>
              </div>

              {/* Hover action buttons */}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                {/* Chat */}
                <button title={`Chat with ${name}`}
                  onClick={() => handleSelectDM({ id: u.id, name, user: u })}
                  className="w-7 h-7 rounded-full bg-white/10 hover:bg-[#00FF99]/20 border border-white/10 hover:border-[#00FF99]/40 flex items-center justify-center transition">
                  <MessageCircle size={14} strokeWidth={1.9} />
                </button>
                {/* Audio call */}
                <button title={`Audio call ${name}`}
                  disabled={!!callingId}
                  onClick={() => handleCall(u, true)}
                  className="w-7 h-7 rounded-full bg-white/10 hover:bg-[#00FF99]/20 border border-white/10 hover:border-[#00FF99]/40 flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed">
                  {isCalling ? <Loader2 size={14} strokeWidth={1.9} className="animate-spin" /> : <Phone size={14} strokeWidth={1.9} />}
                </button>
                {/* Video call */}
                <button title={`Video call ${name}`}
                  disabled={!!callingId}
                  onClick={() => handleCall(u, false)}
                  className="w-7 h-7 rounded-full bg-white/10 hover:bg-[#00FF99]/20 border border-white/10 hover:border-[#00FF99]/40 flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed">
                  {isCalling ? <Loader2 size={14} strokeWidth={1.9} className="animate-spin" /> : <Video size={14} strokeWidth={1.9} />}
                </button>
              </div>
            </div>
          );
        })}

        {!loadingUsers && filtered.length === 0 && (
          <div className="text-center text-white/30 text-sm py-6 px-3">
            {q ? (
              `No colleagues match "${search.trim()}"`
            ) : dirError && users.length === 0 ? (
              "Couldn't load your organisation directory. Please refresh to try again."
            ) : directory.length === 0 && users.length === 0 ? (
              "No colleagues found in your organisation"
            ) : (
              "No conversations yet — search above to find a colleague"
            )}
          </div>
        )}
      </div>
      {/* Recordings panel — starts below the icon row so icons remain visible */}
      {showRecordings && (
        <div style={{ position:"absolute", top:64, left:0, right:0, bottom:0, zIndex:100, borderRadius:"inherit", overflow:"hidden" }}>
          <RecordingsPanel onClose={() => setShowRecordings(false)} />
        </div>
      )}
      {/* Call history panel — starts below the icon row so icons remain visible */}
      {showCallHistory && (
        <div style={{ position:"absolute", top:64, left:0, right:0, bottom:0, zIndex:100, borderRadius:"inherit", overflow:"hidden" }}>
          <CallHistoryPanel
            type={showCallHistory}
            onClose={() => setShowCallHistory(null)}
            onCall={(targetUser, audioOnly) => {
              setShowCallHistory(null);
              handleCall(targetUser, audioOnly);
            }}
          />
        </div>
      )}

      {/* New-chat modal — pick any colleague from the org directory */}
      {showNewChat && (
        <NewChatModal
          onClose={() => setShowNewChat(false)}
          onSelect={(u) => {
            setShowNewChat(false);
            handleSelectDM({ id: u.id, name: u.full_name || u.name || u.email || "Unknown", user: u });
          }}
        />
      )}
    </div>
  );
};

// Mode-switcher CSS (messenger_a2 spec): hover state + hover tooltip pill.
// Active-state colours are applied inline; :not(.k-on) keeps the green glow
// intact while hovering an active icon.
const MSGR_MODE_CSS = `
.kwm-modes{display:flex;gap:4px;align-items:center}
.kwm-md:not(.k-on):hover{color:#fff !important;background:rgba(255,255,255,0.05) !important}
.kwm-md::after{content:attr(data-label);position:absolute;top:calc(100% + 7px);left:50%;transform:translateX(-50%);background:#131313;border:1px solid rgba(255,255,255,0.10);color:#fff;font-size:10px;font-weight:600;padding:4px 10px;border-radius:99px;white-space:nowrap;opacity:0;pointer-events:none;transition:.15s;z-index:30}
.kwm-md:hover::after{opacity:1}
`;

// Single mode icon — 34px, green-glow active state, tooltip pill via data-label.
const ModeIcon = ({ Icon, label, onClick, active = false }) => (
  <button
    type="button"
    aria-label={label}
    data-label={label}
    onClick={onClick}
    className={`kwm-md${active ? " k-on" : ""}`}
    style={{
      position:     "relative",
      width:        34,
      height:       34,
      borderRadius: 11,
      display:      "grid",
      placeItems:   "center",
      cursor:       "pointer",
      padding:      0,
      border:       `1px solid ${active ? "rgba(0,255,153,0.30)" : "transparent"}`,
      background:   active ? "rgba(0,255,153,0.12)" : "transparent",
      color:        active ? "#00FF99" : "rgba(255,255,255,0.55)",
      boxShadow:    active ? "0 0 10px rgba(0,255,153,0.30)" : "none",
      transition:   "all 0.15s",
    }}
  >
    <Icon size={16} strokeWidth={1.8} />
  </button>
);

export default LeftChatList;